import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useCycle } from './CycleContext';
import { differenceInDays, parseISO, format, addDays, subDays, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';

interface CycleInsight {
  id: number;
  user_id: string;
  insight_type: string;
  insight_data: any;
  generated_at: string;
  is_read: boolean;
  created_at: string;
}

interface GeneratedInsight {
  id: string;
  type: 'cycle_prediction' | 'pattern_analysis' | 'symptom_correlation' | 'health_tip' | 'mood_pattern' | 'flow_analysis' | 'lifestyle_correlation' | 'prediction_accuracy' | 'data_quality';
  title: string;
  description: string;
  data: any;
  priority: 'high' | 'medium' | 'low';
  actionable: boolean;
  recommendation?: string;
  confidence: number; // 0-100
  trend?: 'improving' | 'stable' | 'concerning';
  visualData?: any;
  dataQuality?: 'excellent' | 'good' | 'fair' | 'poor';
  validationFlags?: string[];
}

interface DataValidationResult {
  isValid: boolean;
  confidence: number;
  flags: string[];
  severity: 'low' | 'medium' | 'high';
}

interface CycleStats {
  totalCycles: number;
  averageCycleLength: number;
  cycleVariability: number;
  averagePeriodLength: number;
  mostCommonSymptoms: Array<{ symptom: string; frequency: number; percentage: number }>;
  moodPatterns: Record<string, number>;
  flowPatterns: Record<string, number>;
  phaseAnalysis: {
    menstrual: { avgLength: number; commonSymptoms: string[]; commonMoods: string[] };
    follicular: { avgLength: number; commonSymptoms: string[]; commonMoods: string[] };
    ovulation: { avgLength: number; commonSymptoms: string[]; commonMoods: string[] };
    luteal: { avgLength: number; commonSymptoms: string[]; commonMoods: string[] };
  };
  predictionAccuracy: number;
  trackingConsistency: number;
  dataQuality: number;
}

interface InsightsContextType {
  insights: CycleInsight[];
  generatedInsights: GeneratedInsight[];
  cycleStats: CycleStats | null;
  unreadCount: number;
  loading: boolean;
  error: string | null;
  fetchInsights: () => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  generateInsight: (type: string, data: any) => Promise<void>;
  analyzeUserData: () => Promise<void>;
  refreshInsights: () => Promise<void>;
}

const defaultCycleStats: CycleStats = {
  totalCycles: 0,
  averageCycleLength: 28,
  cycleVariability: 0,
  averagePeriodLength: 5,
  mostCommonSymptoms: [],
  moodPatterns: {},
  flowPatterns: {},
  phaseAnalysis: {
    menstrual: { avgLength: 5, commonSymptoms: [], commonMoods: [] },
    follicular: { avgLength: 9, commonSymptoms: [], commonMoods: [] },
    ovulation: { avgLength: 3, commonSymptoms: [], commonMoods: [] },
    luteal: { avgLength: 11, commonSymptoms: [], commonMoods: [] }
  },
  predictionAccuracy: 0,
  trackingConsistency: 0,
  dataQuality: 0
};

const InsightsContext = createContext<InsightsContextType | undefined>(undefined);

export const useInsights = () => {
  const context = useContext(InsightsContext);
  if (context === undefined) {
    throw new Error('useInsights must be used within an InsightsProvider');
  }
  return context;
};

interface InsightsProviderProps {
  children: ReactNode;
}

export const InsightsProvider = ({ children }: InsightsProviderProps) => {
  const [insights, setInsights] = useState<CycleInsight[]>([]);
  const [generatedInsights, setGeneratedInsights] = useState<GeneratedInsight[]>([]);
  const [cycleStats, setCycleStats] = useState<CycleStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();
  const { logs, stats } = useCycle();

  const unreadCount = insights.filter(insight => !insight.is_read).length;

  // Enhanced data validation system
  const validateCycleData = (logs: any[]): DataValidationResult => {
    const flags: string[] = [];
    let confidence = 100;
    let severity: 'low' | 'medium' | 'high' = 'low';

    if (logs.length === 0) {
      return { isValid: false, confidence: 0, flags: ['no_data'], severity: 'high' };
    }

    // Check for period start dates
    const periodStarts = logs.filter(log => log.period_start);
    
    if (periodStarts.length < 2) {
      flags.push('insufficient_period_data');
      confidence -= 30;
      severity = 'medium';
    }

    // Validate cycle lengths
    if (periodStarts.length >= 2) {
      const cycleLengths = [];
      for (let i = 1; i < periodStarts.length; i++) {
        const length = differenceInDays(
          parseISO(periodStarts[i].date),
          parseISO(periodStarts[i - 1].date)
        );
        cycleLengths.push(length);
      }

      // Check for unrealistic cycle lengths
      const unrealisticCycles = cycleLengths.filter(length => length < 15 || length > 45);
      if (unrealisticCycles.length > 0) {
        flags.push('unrealistic_cycle_lengths');
        confidence -= 25;
        severity = 'high';
      }

      // Check for extremely irregular cycles
      const avgLength = cycleLengths.reduce((sum, length) => sum + length, 0) / cycleLengths.length;
      const variance = cycleLengths.reduce((sum, length) => sum + Math.pow(length - avgLength, 2), 0) / cycleLengths.length;
      const standardDeviation = Math.sqrt(variance);

      if (standardDeviation > 10) {
        flags.push('highly_irregular_cycles');
        confidence -= 20;
        severity = severity === 'high' ? 'high' : 'medium';
      }
    }

    // Check for data consistency
    const recentLogs = logs.filter(log => 
      differenceInDays(new Date(), parseISO(log.date)) <= 90
    );

    // Check for gaps in data
    const sortedLogs = recentLogs.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    let gapCount = 0;
    for (let i = 1; i < sortedLogs.length; i++) {
      const daysBetween = differenceInDays(parseISO(sortedLogs[i].date), parseISO(sortedLogs[i-1].date));
      if (daysBetween > 7) {
        gapCount++;
      }
    }

    if (gapCount > 3) {
      flags.push('inconsistent_tracking');
      confidence -= 15;
    }

    // Check for contradictory data
    const periodsWithoutFlow = logs.filter(log => 
      log.period_start && (!log.flow_level || log.flow_level === 'light')
    );
    
    if (periodsWithoutFlow.length > periodStarts.length * 0.3) {
      flags.push('contradictory_period_flow_data');
      confidence -= 20;
      severity = 'medium';
    }

    // Check for unrealistic symptom patterns
    const allSymptoms = logs.flatMap(log => log.symptoms || []);
    const symptomCounts = allSymptoms.reduce((acc, symptom) => {
      acc[symptom] = (acc[symptom] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // If user reports the same symptoms every single day, it might be inaccurate
    const totalLogsWithSymptoms = logs.filter(log => log.symptoms && log.symptoms.length > 0).length;
    const mostCommonSymptom = Object.values(symptomCounts).sort((a, b) => b - a)[0] || 0;
    
    if (totalLogsWithSymptoms > 10 && mostCommonSymptom > totalLogsWithSymptoms * 0.9) {
      flags.push('unrealistic_symptom_consistency');
      confidence -= 15;
    }

    // Check for future dates
    const futureLogs = logs.filter(log => parseISO(log.date) > new Date());
    if (futureLogs.length > 0) {
      flags.push('future_dates_detected');
      confidence -= 30;
      severity = 'high';
    }

    // Check for duplicate dates
    const dates = logs.map(log => log.date);
    const uniqueDates = new Set(dates);
    if (dates.length !== uniqueDates.size) {
      flags.push('duplicate_dates');
      confidence -= 10;
    }

    return {
      isValid: confidence > 50,
      confidence: Math.max(0, confidence),
      flags,
      severity
    };
  };

  const calculateCycleStats = (): CycleStats => {
    if (!logs || logs.length === 0) return defaultCycleStats;

    const validation = validateCycleData(logs);
    const dataQuality = validation.confidence;

    const periodStarts = logs
      .filter(log => log.period_start)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const totalCycles = periodStarts.length;
    
    // Calculate cycle lengths with validation
    const cycleLengths = [];
    for (let i = 1; i < periodStarts.length; i++) {
      const length = differenceInDays(
        parseISO(periodStarts[i].date),
        parseISO(periodStarts[i - 1].date)
      );
      // Only include realistic cycle lengths
      if (length >= 15 && length <= 45) {
        cycleLengths.push(length);
      }
    }

    const averageCycleLength = cycleLengths.length > 0 
      ? cycleLengths.reduce((sum, length) => sum + length, 0) / cycleLengths.length 
      : 28;

    const cycleVariability = cycleLengths.length > 1
      ? Math.sqrt(cycleLengths.reduce((sum, length) => sum + Math.pow(length - averageCycleLength, 2), 0) / cycleLengths.length)
      : 0;

    // Analyze symptoms with validation
    const symptomCounts: Record<string, number> = {};
    const validLogs = logs.filter(log => !validation.flags.includes('future_dates_detected') || parseISO(log.date) <= new Date());
    const totalLogsWithSymptoms = validLogs.filter(log => log.symptoms && log.symptoms.length > 0).length;
    
    validLogs.forEach(log => {
      if (log.symptoms) {
        log.symptoms.forEach(symptom => {
          symptomCounts[symptom] = (symptomCounts[symptom] || 0) + 1;
        });
      }
    });

    const mostCommonSymptoms = Object.entries(symptomCounts)
      .map(([symptom, count]) => ({
        symptom,
        frequency: count,
        percentage: totalLogsWithSymptoms > 0 ? Math.round((count / totalLogsWithSymptoms) * 100) : 0
      }))
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, 10);

    // Analyze moods with validation
    const moodCounts: Record<string, number> = {};
    validLogs.forEach(log => {
      if (log.mood) {
        moodCounts[log.mood] = (moodCounts[log.mood] || 0) + 1;
      }
    });

    // Analyze flow patterns with validation
    const flowCounts: Record<string, number> = {};
    validLogs.forEach(log => {
      if (log.flow_level) {
        flowCounts[log.flow_level] = (flowCounts[log.flow_level] || 0) + 1;
      }
    });

    // Phase analysis with validation
    const phaseData = {
      menstrual: { symptoms: [] as string[], moods: [] as string[], lengths: [] as number[] },
      follicular: { symptoms: [] as string[], moods: [] as string[], lengths: [] as number[] },
      ovulation: { symptoms: [] as string[], moods: [] as string[], lengths: [] as number[] },
      luteal: { symptoms: [] as string[], moods: [] as string[], lengths: [] as number[] }
    };

    validLogs.forEach(log => {
      const logDate = parseISO(log.date);
      const lastPeriod = periodStarts.find(p => parseISO(p.date) <= logDate);
      
      if (lastPeriod) {
        const daysSincePeriod = differenceInDays(logDate, parseISO(lastPeriod.date));
        let phase: keyof typeof phaseData = 'follicular';
        
        if (daysSincePeriod <= 5) phase = 'menstrual';
        else if (daysSincePeriod <= 13) phase = 'follicular';
        else if (daysSincePeriod <= 15) phase = 'ovulation';
        else phase = 'luteal';
        
        if (log.symptoms) phaseData[phase].symptoms.push(...log.symptoms);
        if (log.mood) phaseData[phase].moods.push(log.mood);
      }
    });

    const phaseAnalysis = {
      menstrual: {
        avgLength: 5,
        commonSymptoms: getTopItems(phaseData.menstrual.symptoms, 5),
        commonMoods: getTopItems(phaseData.menstrual.moods, 3)
      },
      follicular: {
        avgLength: 9,
        commonSymptoms: getTopItems(phaseData.follicular.symptoms, 5),
        commonMoods: getTopItems(phaseData.follicular.moods, 3)
      },
      ovulation: {
        avgLength: 3,
        commonSymptoms: getTopItems(phaseData.ovulation.symptoms, 5),
        commonMoods: getTopItems(phaseData.ovulation.moods, 3)
      },
      luteal: {
        avgLength: 11,
        commonSymptoms: getTopItems(phaseData.luteal.symptoms, 5),
        commonMoods: getTopItems(phaseData.luteal.moods, 3)
      }
    };

    // Calculate tracking consistency
    const threeMonthsAgo = subDays(new Date(), 90);
    const recentLogs = validLogs.filter(log => parseISO(log.date) >= threeMonthsAgo);
    const trackingConsistency = Math.min(100, Math.round((recentLogs.length / 90) * 100));

    // Calculate prediction accuracy based on data quality
    const predictionAccuracy = Math.min(95, Math.max(30, 
      cycleVariability <= 2 ? 95 - (100 - dataQuality) * 0.1 : 
      cycleVariability <= 5 ? 80 - (100 - dataQuality) * 0.2 : 
      60 - (100 - dataQuality) * 0.3
    ));

    return {
      totalCycles,
      averageCycleLength: Math.round(averageCycleLength),
      cycleVariability: Math.round(cycleVariability * 10) / 10,
      averagePeriodLength: 5,
      mostCommonSymptoms,
      moodPatterns: moodCounts,
      flowPatterns: flowCounts,
      phaseAnalysis,
      predictionAccuracy: Math.round(predictionAccuracy),
      trackingConsistency,
      dataQuality: Math.round(dataQuality)
    };
  };

  const getTopItems = (items: string[], count: number): string[] => {
    const counts: Record<string, number> = {};
    items.forEach(item => {
      counts[item] = (counts[item] || 0) + 1;
    });
    
    return Object.entries(counts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, count)
      .map(([item]) => item);
  };

  const analyzeUserData = async () => {
    if (!logs || logs.length === 0) {
      setGeneratedInsights([]);
      setCycleStats(defaultCycleStats);
      return;
    }

    const validation = validateCycleData(logs);
    const stats = calculateCycleStats();
    setCycleStats(stats);

    const newInsights: GeneratedInsight[] = [];

    // 1. Data Quality Assessment (Always first)
    if (validation.flags.length > 0) {
      let dataQualityTitle = 'Data Quality Assessment';
      let dataQualityDescription = '';
      let priority: 'high' | 'medium' | 'low' = 'low';
      let recommendation = '';

      if (validation.severity === 'high') {
        priority = 'high';
        dataQualityTitle = '⚠️ Data Quality Concerns Detected';
        dataQualityDescription = `We've identified some potential issues with your cycle data that may affect the accuracy of our insights. `;
        
        if (validation.flags.includes('unrealistic_cycle_lengths')) {
          dataQualityDescription += 'Some cycle lengths appear unusually short or long (less than 15 or more than 45 days). ';
        }
        if (validation.flags.includes('future_dates_detected')) {
          dataQualityDescription += 'Future dates were detected in your logs. ';
        }
        if (validation.flags.includes('contradictory_period_flow_data')) {
          dataQualityDescription += 'Some period days show inconsistent flow data. ';
        }
        
        recommendation = 'Please review your recent entries for accuracy. Ensure dates are correct and period days include appropriate flow levels. Accurate data helps us provide better insights for your health.';
      } else if (validation.severity === 'medium') {
        priority = 'medium';
        dataQualityDescription = `Your data quality is good but could be improved. `;
        
        if (validation.flags.includes('insufficient_period_data')) {
          dataQualityDescription += 'More period start dates will help improve prediction accuracy. ';
        }
        if (validation.flags.includes('inconsistent_tracking')) {
          dataQualityDescription += 'More consistent daily tracking will enhance insights. ';
        }
        
        recommendation = 'Continue tracking consistently and ensure you mark period start dates accurately. This will significantly improve our analysis quality.';
      } else {
        dataQualityDescription = `Your data quality is excellent! We have high confidence in our analysis based on your consistent and accurate tracking.`;
        recommendation = 'Keep up the excellent tracking habits! Your data quality enables us to provide highly accurate insights and predictions.';
      }

      newInsights.push({
        id: 'data-quality-assessment',
        type: 'data_quality',
        title: dataQualityTitle,
        description: dataQualityDescription,
        data: { 
          quality: stats.dataQuality,
          flags: validation.flags,
          severity: validation.severity
        },
        priority,
        actionable: validation.flags.length > 0,
        confidence: 100, // We're always confident about data quality assessment
        trend: 'stable',
        recommendation,
        dataQuality: stats.dataQuality >= 80 ? 'excellent' : stats.dataQuality >= 60 ? 'good' : stats.dataQuality >= 40 ? 'fair' : 'poor',
        validationFlags: validation.flags,
        visualData: {
          chartType: 'gauge',
          data: {
            value: stats.dataQuality,
            max: 100,
            label: 'Data Quality Score'
          }
        }
      });
    }

    // Only proceed with other insights if data quality is reasonable
    if (validation.confidence >= 40) {
      // 2. Cycle Regularity Analysis with enhanced validation
      if (stats.totalCycles >= 2) {
        let regularity: 'excellent' | 'good' | 'moderate' | 'irregular' = 'irregular';
        let confidence = Math.min(95, validation.confidence);
        let trend: 'improving' | 'stable' | 'concerning' = 'stable';

        if (stats.cycleVariability <= 1) {
          regularity = 'excellent';
          confidence = Math.min(95, confidence);
          trend = 'stable';
        } else if (stats.cycleVariability <= 3) {
          regularity = 'good';
          confidence = Math.min(85, confidence);
          trend = 'stable';
        } else if (stats.cycleVariability <= 6) {
          regularity = 'moderate';
          confidence = Math.min(70, confidence);
          trend = stats.cycleVariability > 5 ? 'concerning' : 'stable';
        } else {
          regularity = 'irregular';
          confidence = Math.min(50, confidence);
          trend = 'concerning';
        }

        // Adjust confidence based on data quality
        confidence = Math.round(confidence * (stats.dataQuality / 100));

        let description = `Your cycle varies by ±${stats.cycleVariability.toFixed(1)} days on average. `;
        
        if (validation.flags.includes('unrealistic_cycle_lengths')) {
          description += '⚠️ Note: Some cycle lengths in your data appear unusual, which may affect this analysis. ';
          confidence = Math.max(30, confidence - 20);
        }

        description += regularity === 'excellent' ? 'This exceptional consistency makes predictions highly accurate!' :
          regularity === 'good' ? 'This good consistency allows for reliable predictions.' :
          regularity === 'moderate' ? 'Some variation is normal, but tracking helps improve predictions.' :
          'Significant variation detected. Please verify your data accuracy, as this could indicate data entry errors or underlying health conditions.';

        newInsights.push({
          id: 'cycle-regularity',
          type: 'cycle_prediction',
          title: `${regularity.charAt(0).toUpperCase() + regularity.slice(1)} Cycle Regularity`,
          description,
          data: { 
            regularity, 
            variance: stats.cycleVariability, 
            avgLength: stats.averageCycleLength,
            totalCycles: stats.totalCycles,
            dataQuality: stats.dataQuality
          },
          priority: regularity === 'irregular' ? 'high' : regularity === 'moderate' ? 'medium' : 'low',
          actionable: regularity === 'irregular' || regularity === 'moderate',
          confidence,
          trend,
          recommendation: regularity === 'irregular' 
            ? 'First, please review your logged period start dates for accuracy. If data is correct, track stress levels, sleep quality, exercise, and diet to identify factors affecting cycle regularity. Consider consulting a healthcare provider if irregularity persists.'
            : regularity === 'moderate'
            ? 'Continue consistent tracking and note any lifestyle changes that coincide with cycle variations. Ensure all period start dates are accurately marked.'
            : 'Maintain your current tracking habits to preserve prediction accuracy.',
          dataQuality: stats.dataQuality >= 80 ? 'excellent' : stats.dataQuality >= 60 ? 'good' : 'fair',
          validationFlags: validation.flags,
          visualData: {
            chartType: 'line',
            data: logs.filter(log => log.period_start).map((log, index) => ({
              cycle: index + 1,
              date: log.date,
              length: index > 0 ? differenceInDays(parseISO(log.date), parseISO(logs.filter(l => l.period_start)[index - 1].date)) : null
            })).filter(d => d.length !== null && d.length >= 15 && d.length <= 45) // Filter out unrealistic lengths
          }
        });
      }

      // 3. Enhanced Symptom Pattern Analysis
      if (stats.mostCommonSymptoms.length > 0) {
        const topSymptom = stats.mostCommonSymptoms[0];
        const symptomTrend = topSymptom.percentage > 60 ? 'frequent' : topSymptom.percentage > 30 ? 'moderate' : 'occasional';
        
        let confidence = Math.min(90, validation.confidence);
        let description = `You experience ${topSymptom.symptom.replace('_', ' ')} in ${topSymptom.percentage}% of your logged cycles (${topSymptom.frequency} times). `;
        
        // Check for unrealistic symptom patterns
        if (validation.flags.includes('unrealistic_symptom_consistency')) {
          description += '⚠️ Note: This symptom appears very consistently in your logs. Please verify this is accurate, as some variation is typically expected. ';
          confidence = Math.max(40, confidence - 25);
        }

        description += symptomTrend === 'frequent' ? 'This is your most consistent symptom pattern.' :
          symptomTrend === 'moderate' ? 'This symptom appears regularly in your cycles.' :
          'This symptom occurs occasionally but is worth monitoring.';

        newInsights.push({
          id: 'symptom-patterns',
          type: 'symptom_correlation',
          title: `Primary Symptom: ${topSymptom.symptom.replace('_', ' ').toUpperCase()}`,
          description,
          data: { 
            primarySymptom: topSymptom,
            allSymptoms: stats.mostCommonSymptoms.slice(0, 5),
            trend: symptomTrend,
            dataQuality: stats.dataQuality
          },
          priority: symptomTrend === 'frequent' ? 'medium' : 'low',
          actionable: true,
          confidence: Math.round(confidence * (stats.dataQuality / 100)),
          trend: 'stable',
          recommendation: `Track when ${topSymptom.symptom.replace('_', ' ')} occurs in your cycle to identify patterns and develop management strategies. ${
            topSymptom.symptom.includes('pain') || topSymptom.symptom.includes('cramp') 
              ? 'Consider heat therapy, gentle exercise, or over-the-counter pain relief.' 
              : topSymptom.symptom.includes('mood') 
              ? 'Practice stress management techniques and ensure adequate sleep.' 
              : 'Monitor this symptom and note any triggers or relief methods.'
          } If patterns seem unusual, verify your tracking accuracy.`,
          dataQuality: stats.dataQuality >= 80 ? 'excellent' : stats.dataQuality >= 60 ? 'good' : 'fair',
          validationFlags: validation.flags,
          visualData: {
            chartType: 'bar',
            data: stats.mostCommonSymptoms.slice(0, 5).map(s => ({
              symptom: s.symptom.replace('_', ' '),
              frequency: s.frequency,
              percentage: s.percentage
            }))
          }
        });
      }

      // 4. Enhanced Prediction Accuracy Report
      if (stats.totalCycles >= 3) {
        let confidence = Math.min(95, validation.confidence);
        let description = `Our cycle predictions are ${stats.predictionAccuracy}% accurate based on your data quality and cycle regularity. `;
        
        if (stats.dataQuality < 70) {
          description += '⚠️ Prediction accuracy is limited by data quality concerns. ';
          confidence = Math.max(30, confidence - 30);
        }

        description += stats.predictionAccuracy >= 90 ? 'Exceptional accuracy due to your regular cycle and consistent tracking!' :
          stats.predictionAccuracy >= 75 ? 'Good accuracy. Continue tracking to maintain prediction quality.' :
          'Moderate accuracy. Improving data quality and consistency will enhance predictions.';

        newInsights.push({
          id: 'prediction-accuracy',
          type: 'prediction_accuracy',
          title: `Prediction Accuracy: ${stats.predictionAccuracy}%`,
          description,
          data: { 
            accuracy: stats.predictionAccuracy,
            cycleRegularity: stats.cycleVariability,
            trackingQuality: stats.trackingConsistency,
            dataQuality: stats.dataQuality
          },
          priority: stats.predictionAccuracy < 60 ? 'medium' : 'low',
          actionable: stats.predictionAccuracy < 75,
          confidence: Math.round(confidence * (stats.dataQuality / 100)),
          trend: stats.predictionAccuracy >= 80 ? 'stable' : 'improving',
          recommendation: stats.predictionAccuracy < 75
            ? 'Focus on accurate daily tracking and ensure period start dates are correctly marked. Review recent entries for any errors that might affect predictions.'
            : 'Your predictions are highly reliable! Use them confidently for planning activities and health management.',
          dataQuality: stats.dataQuality >= 80 ? 'excellent' : stats.dataQuality >= 60 ? 'good' : 'fair',
          validationFlags: validation.flags,
          visualData: {
            chartType: 'gauge',
            data: {
              value: stats.predictionAccuracy,
              max: 100,
              label: 'Prediction Accuracy'
            }
          }
        });
      }
    } else {
      // If data quality is very poor, provide guidance
      newInsights.push({
        id: 'data-improvement-guide',
        type: 'health_tip',
        title: '📊 Improve Your Data Quality',
        description: 'Your current data has some accuracy concerns that limit our ability to provide reliable insights. Let\'s work together to improve your tracking for better health analysis.',
        data: { 
          currentQuality: stats.dataQuality,
          improvementNeeded: true
        },
        priority: 'high',
        actionable: true,
        confidence: 100,
        trend: 'stable',
        recommendation: 'Start by reviewing your recent entries for accuracy. Mark period start dates clearly, ensure dates are not in the future, and track consistently. Even a few weeks of accurate data will significantly improve our insights.',
        dataQuality: 'poor',
        validationFlags: validation.flags
      });
    }

    setGeneratedInsights(newInsights);
  };

  const fetchInsights = async () => {
    if (!isAuthenticated || !user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('cycle_insights')
        .select('*')
        .eq('user_id', user.id)
        .order('generated_at', { ascending: false });

      if (fetchError) throw fetchError;
      setInsights(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch insights');
    } finally {
      setLoading(false);
    }
  };

  const refreshInsights = async () => {
    await analyzeUserData();
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchInsights();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (logs.length > 0) {
      analyzeUserData();
    }
  }, [logs]);

  const markAsRead = async (id: number) => {
    try {
      setError(null);
      
      const { error: updateError } = await supabase
        .from('cycle_insights')
        .update({ is_read: true })
        .eq('id', id)
        .eq('user_id', user?.id);

      if (updateError) throw updateError;

      setInsights(prev => 
        prev.map(insight => 
          insight.id === id ? { ...insight, is_read: true } : insight
        )
      );
    } catch (err: any) {
      setError(err.message || 'Failed to mark insight as read');
      throw err;
    }
  };

  const generateInsight = async (type: string, data: any) => {
    if (!user) throw new Error('User not authenticated');

    try {
      setError(null);
      
      const { data: newInsight, error: insertError } = await supabase
        .from('cycle_insights')
        .insert([{
          user_id: user.id,
          insight_type: type,
          insight_data: data,
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      setInsights(prev => [newInsight, ...prev]);
    } catch (err: any) {
      setError(err.message || 'Failed to generate insight');
      throw err;
    }
  };

  return (
    <InsightsContext.Provider
      value={{
        insights,
        generatedInsights,
        cycleStats,
        unreadCount,
        loading,
        error,
        fetchInsights,
        markAsRead,
        generateInsight,
        analyzeUserData,
        refreshInsights,
      }}
    >
      {children}
    </InsightsContext.Provider>
  );
};